import {CopyFile, Edit, TrashCan} from '@carbon/icons-react';
import {
    DataTable,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    DataTableSkeleton,
    IconButton,
    TableToolbar,
    TableToolbarSearch,
    TableToolbarContent,
    TableExpandHeader,
    TableExpandRow,
    RadioButton,
} from '@carbon/react';
import React, {useMemo, useContext} from 'react';
import {useNavigate} from 'react-router-dom';
import {safeConsoleError} from '../../../lib/sanitize';
import {NetworkContext} from '../lib/context';
import FileActionModal from './file-action-modal';
import {getProviderLocationSortedRowsWithExpand, SortState} from './my-files-helper';
import {ParsedApiError} from '../../../lib/error-handler';
import ErrorDisplay from './error-display';

interface ProviderLocationsTableProps {
    rows: any[];
    loading: boolean;
    error?: string | ParsedApiError | null;
    onCopyRoster?: (rosterId: number, rosterName: string, notes: string) => Promise<void>;
    onRenameRoster?: (rosterId: number, revisionId: number, rosterName: string, notes: string) => Promise<void>;
    onDeleteRoster?: (rosterId: number, revisionId: number) => Promise<void>;
}

const headers = [
    {key: 'rosterName', header: 'File Name'},
    {key: 'fileType', header: 'File Type'},
    {key: 'revisionDateModified', header: 'Last Modified', isSortable: true},
    {key: 'revisionModifiedBy', header: 'Modified By'},
    {key: 'revisionNotes', header: 'Notes'},
    {key: 'actions', header: ''},

];

const ProviderLocationsTable: React.FC<ProviderLocationsTableProps> = ({rows, loading, error, onCopyRoster, onRenameRoster, onDeleteRoster}) => {
    const networkStore = useContext(NetworkContext);
    const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);
    const [selectedRadio, setSelectedRadio] = React.useState<string | null>(null);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalMode, setModalMode] = React.useState<'edit' | 'copy' | 'delete' | null>(null);
    const [modalRow, setModalRow] = React.useState<any | null>(null);
    const [sortState, setSortState] = React.useState<SortState>({key: '', direction: 'NONE'});
    const [searchValue, setSearchValue] = React.useState('');
    const navigate = useNavigate();

    const handleAction = (mode: 'edit' | 'copy' | 'delete', row: any) => {
        setModalMode(mode);
        setModalRow(row);
        setModalOpen(true);
    };

    const handleModalSubmit = async (data: {fileName?: string; notes?: string}) => {
        setModalOpen(false);
        if (modalMode === 'copy' && modalRow && onCopyRoster && data.fileName) {
            try {
                await onCopyRoster(modalRow.rosterId, data.fileName, data.notes || '');
            } catch (error) {
                networkStore.handleApiError(error, 'Failed to Copy Roster', () => onCopyRoster(modalRow.rosterId, data.fileName!, data.notes || ''));
            }
        }
        else if (modalMode === 'edit' && modalRow && onRenameRoster && modalRow.revisionId && modalRow.rosterId && data.fileName) {
            try {
                await onRenameRoster(modalRow.rosterId, modalRow.revisionId, data.fileName, data.notes || '');
            } catch (error) {
                networkStore.handleApiError(error, 'Failed to Rename Roster', () => onRenameRoster(modalRow.rosterId, modalRow.revisionId, data.fileName!, data.notes || ''));
            }
        }
        else if (modalMode === 'delete' && modalRow && modalRow.revisionId && modalRow.rosterId) {
            try {
                await onDeleteRoster!(modalRow.rosterId, modalRow.revisionId);
            } catch (error) {
                networkStore.handleApiError(error, 'Failed to Delete Roster');
            }
        }
    };

    const filteredRows = useMemo(() => {
        if (!searchValue) return rows;
        const lower = searchValue.toLowerCase();
        return rows.filter(row =>
            (row.rosterName && row.rosterName.toLowerCase().includes(lower)) ||
            (row.revisionModifiedBy && row.revisionModifiedBy.toLowerCase().includes(lower)) ||
            (row.revisionNotes && row.revisionNotes.toLowerCase().includes(lower))
        );
    }, [rows, searchValue]);

    const rowsWithExpand = useMemo(() => {
        return getProviderLocationSortedRowsWithExpand(filteredRows, sortState);
    }, [filteredRows, sortState]);

    const handleSortHeaderClick = (header: typeof headers[number], headerProps: any) => (e: React.MouseEvent) => {
        if (header.isSortable && headerProps.onClick) {
            headerProps.onClick(e);
            setSortState((prev) => {
                if (prev.key !== header.key) {
                    return {key: header.key, direction: 'ASC'};
                }
                if (prev.direction === 'NONE') {
                    return {key: header.key, direction: 'ASC'};
                }
                if (prev.direction === 'ASC') {
                    return {key: header.key, direction: 'DESC'};
                }
                if (prev.direction === 'DESC') {
                    return {key: '', direction: 'NONE'};
                }
                return {key: header.key, direction: 'ASC'};
            });
        }
    };

    return (
        <>
            {error ? (
                <ErrorDisplay error={error} title='Unable to Load Files' />
            ) : loading ? (
                <DataTableSkeleton
                    headers={headers}
                    columnCount={headers.length}
                    rowCount={12}
                    showHeader={false}
                    showToolbar={false}
                />
            ) : (
                <DataTable
                    rows={rowsWithExpand}
                    headers={headers}
                >
                    {({getTableContainerProps, getRowProps, getExpandHeaderProps, getHeaderProps, getTableProps}) => (
                        <TableContainer data-testid="provider-locations-table-container" {...getTableContainerProps()}>
                            <TableToolbar size='sm' aria-label="data table toolbar">
                                <TableToolbarContent style={{backgroundColor: 'white'}} >
                                    <TableToolbarSearch
                                        placeholder='Filter Locations'
                                        onChange={(e: any) => setSearchValue(e.target.value)}
                                    />
                                </TableToolbarContent>
                            </TableToolbar>
                            <Table {...getTableProps()}>
                                <TableHead>
                                    <TableRow>
                                        <TableExpandHeader {...getExpandHeaderProps()} />
                                        {headers.map((header, i) => {
                                            let headerProps = getHeaderProps({header});
                                            return (
                                                <TableHeader
                                                    {...headerProps}
                                                    key={i}
                                                    isSortable={header.isSortable}
                                                    onClick={handleSortHeaderClick(header, headerProps)}
                                                >
                                                    {header.header}
                                                </TableHeader>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rowsWithExpand.map((row: any) => {
                                        const rowProps = getRowProps({row});
                                        const {key, ...restRowProps} = rowProps;
                                        return (
                                            <React.Fragment key={key}>
                                                <TableExpandRow
                                                    {...restRowProps}
                                                    isExpanded={expandedRowId === row.id}
                                                    onExpand={() => setExpandedRowId(expandedRowId === row.id ? null : row.id)}
                                                    aria-label="Expand row"
                                                    style={{backgroundColor: '#ffffff'}}
                                                >
                                                    <TableCell>{row.rosterName}</TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>{row.lastModified ? new Date(row.lastModified).toLocaleString() : '-'}</TableCell>
                                                    <TableCell>{row.revisionModifiedBy || '-'}</TableCell>
                                                    <TableCell />
                                                    <TableCell />
                                                </TableExpandRow>
                                                {expandedRowId === row.id && (
                                                    row.children.map((childRow: any) => (
                                                        <TableRow key={childRow.revisionId}
                                                            style={{backgroundColor: '#ffffff'}}
                                                            data-testid="expanded-row"
                                                        >
                                                            <TableCell />
                                                            <TableCell>
                                                                <RadioButton
                                                                    id={`radio-${childRow.rosterId}-${childRow.revisionId}`}
                                                                    name="expanded-row-radio"
                                                                    labelText={`${childRow.rosterName}${childRow.revisionId !== '0' ? ` - ${childRow.revisionName}` : ''}`}
                                                                    checked={selectedRadio === (childRow.rosterId + '-' + childRow.revisionId)}
                                                                    onChange={() => {
                                                                        setSelectedRadio(childRow.rosterId + '-' + childRow.revisionId);
                                                                        navigate(`../${childRow.rosterId}/${childRow.revisionId}/network-analysis`);
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>{childRow.revisionId === '0' ? 'Original' : 'Revised'}</TableCell>
                                                            <TableCell>{childRow.revisionDateModified ? new Date(childRow.revisionDateModified).toLocaleString() : '-'}</TableCell>
                                                            <TableCell>{childRow.revisionModifiedBy || '-'}</TableCell>
                                                            <TableCell>{childRow.revisionNotes || '-'}</TableCell>
                                                            <TableCell className='text-end'>
                                                                <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.5rem'}}>
                                                                    <IconButton kind='ghost' label='Copy File' onClick={() => handleAction('copy', childRow)} data-testid='copy-file-icon-btn'><CopyFile /></IconButton>
                                                                    {childRow.revisionId !== '0' && <IconButton kind='ghost' label='Edit File Info' onClick={() => handleAction('edit', childRow)} data-testid='edit-file-icon-btn'><Edit /></IconButton>}
                                                                    {childRow.revisionId !== '0' && <IconButton kind='ghost' label='Delete File' onClick={() => handleAction('delete', childRow)} data-testid='delete-file-icon-btn'><TrashCan /></IconButton>}
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DataTable>

            )}
            {modalRow && (
                <FileActionModal
                    open={modalOpen}
                    mode={modalMode || 'edit'}
                    initialFileName={modalRow.revisionName}
                    initialNotes={modalRow?.notes}
                    onClose={() => setModalOpen(false)}
                    onSubmit={handleModalSubmit}
                />
            )}
        </>
    );
};

export default ProviderLocationsTable;
